import React, { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import UnityPlayer, { TrackedComponent } from './UnityPlayer'
import AccessibleMultipleChoiceGame from './AccessibleMultipleChoiceGame'
import DaxAnimation, { DaxReaction, DAX_CANVAS_FILL } from './DaxAnimation/DaxAnimation'
import TypewriterText from '../molecules/TypewriterText/TypewriterText'
import { PostAnswerData, McqGameInputData } from '../../models/global.types'
// PreferenceType import removed as unused
import { logger } from '../../utils'
import { useUserPreferences } from '../../contexts/UserPreferencesContext'
import { hasDaxAnimationFeatureFlag } from '../molecules/URLFeatureFlag/URLFeatureFlag'

interface MultipleChoiceQuestionsProps {
  accessibilityView: boolean
  multipleChoiceQuestionData: McqGameInputData | null
  onEndGame: (questionAndAnswerData: PostAnswerData[]) => Promise<void> | void
  onNewLightningModule: (level: string) => void
  onUpdateXP: (questionAndAnswerData: PostAnswerData[]) => void
  trackedComponents?: TrackedComponent[]
}

const MultipleChoiceQuestions: React.FC<MultipleChoiceQuestionsProps> = ({
  accessibilityView,
  multipleChoiceQuestionData,
  onEndGame,
  onNewLightningModule,
  onUpdateXP, // only fires when questions are answered correctly.
  trackedComponents = []
}: MultipleChoiceQuestionsProps) => {
  const [payloadToUnity, setPayloadToUnity] = useState<any>(null)
  const [gameEnded, setGameEnded] = useState<boolean>(false)
  const [reduceMotion, setReduceMotion] = useState<boolean>(false)
  const [daxReaction, setDaxReaction] = useState<DaxReaction>('idle')
  const [daxReactionKey, setDaxReactionKey] = useState<number>(0)
  // Accessible-view Dax height (px): 3/5 of the gap between the health bar and buttons.
  const [daxAccessibleHeight, setDaxAccessibleHeight] = useState<number | null>(null)
  // Accessible-view Dax box top (px, relative to the container): positioned so Dax's
  // feet land on the top edge of the RESET/GO buttons (he stands on the same level).
  const [daxAccessibleTop, setDaxAccessibleTop] = useState<number | null>(null)
  const daxContainerRef = useRef<HTMLDivElement>(null)
  // Wrong-answer speech bubble by Dax (accessible view only). Keyed so re-answering
  // wrong remounts the message and re-runs the typewriter from the start.
  const [showWrongBubble, setShowWrongBubble] = useState<boolean>(false)
  const [wrongBubbleKey, setWrongBubbleKey] = useState<number>(0)
  const userPreferences = useUserPreferences()
  const responsiveUnity = 'h-[90vh]'

  // Show Dax wherever the feature is enabled - he stays on screen for the whole
  // quiz, independent of answers. (reduceMotion is still passed through so his
  // celebratory reaction is suppressed, but he remains visible.)
  const showDax = hasDaxAnimationFeatureFlag()

  // Fire Dax's celebratory reaction; bump the key so it re-triggers every correct answer.
  const triggerDaxCorrect = (): void => {
    setDaxReaction('correct')
    setDaxReactionKey((key) => key + 1)
    setShowWrongBubble(false)
  }

  // Show Dax's wrong-answer speech bubble (accessible view).
  const triggerWrongBubble = (): void => {
    setShowWrongBubble(true)
    setWrongBubbleKey((key) => key + 1)
  }

  // The wrong-answer bubble stays visible once shown; it only clears on a correct
  // answer (triggerDaxCorrect) or is remounted by another wrong answer.

  // Sync reduce motion preference from context
  useEffect(() => {
    setReduceMotion(userPreferences.preferenceA11yReduceMotion)
  }, [userPreferences.preferenceA11yReduceMotion])

  // Per the ticket, accessible-view Dax stands at 3/5 of the vertical space between the
  // health bar (top) and the RESET/GO buttons (bottom). Both are DOM here, so measure the
  // live gap and keep it in sync on resize.
  useEffect(() => {
    if (!accessibilityView || !showDax) return

    const measure = (): void => {
      const container = daxContainerRef.current
      const healthBar = trackedComponents.find((c) => c.id === 'pointBar')?.ref.current
      const goButton = document.getElementById('button-go')
      if (container == null || healthBar == null || goButton == null) return
      const goRect = goButton.getBoundingClientRect()
      const gap = goRect.top - healthBar.getBoundingClientRect().bottom
      if (gap <= 0) return
      // Dax's VISIBLE body should be 3/5 of the gap. He fills DAX_CANVAS_FILL of his
      // canvas, so size the box up accordingly.
      const boxHeight = (gap * (3 / 5)) / DAX_CANVAS_FILL
      setDaxAccessibleHeight(boxHeight)
      // Align Dax's feet with the TOP of the RESET/GO buttons so they read as being on
      // the same level. Dax is vertically centered in his (taller) canvas box, so his
      // feet sit a margin above the box bottom; shift the box up by that margin.
      const footMargin = (boxHeight * (1 - DAX_CANVAS_FILL)) / 2
      const buttonTopInContainer = goRect.top - container.getBoundingClientRect().top
      setDaxAccessibleTop(buttonTopInContainer - boxHeight + footMargin)
    }

    const raf = requestAnimationFrame(measure)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', measure)
    }
  }, [accessibilityView, showDax, trackedComponents, multipleChoiceQuestionData])

  useEffect(() => {
    if (!accessibilityView) {
      // Update object to format needed for unity player
      let newPayload = null
      if (multipleChoiceQuestionData !== null && multipleChoiceQuestionData !== undefined) {
        logger.log(`🎯 MultipleChoiceQuestions: Building Unity payload with reduceMotion: ${String(reduceMotion)}`)

        newPayload = {
          level_name: multipleChoiceQuestionData.levelName,
          questions: JSON.stringify({ questions: multipleChoiceQuestionData.questions }),
          totalScore: multipleChoiceQuestionData.totalScore,
          accessibility: {
            reduceMotion
          }
        }

        logger.log('🎯 MultipleChoiceQuestions: Complete payload:')
        logger.log(JSON.stringify(newPayload))
      }
      setPayloadToUnity(newPayload)
    }
  }, [accessibilityView, multipleChoiceQuestionData, reduceMotion])

  const handleUnityUpdateXP = (json: string): void => {
    const answerData: PostAnswerData[] = JSON.parse(json).answers
    if (showDax) triggerDaxCorrect()
    onUpdateXP(answerData)
  }

  // Accessible view: wrap onUpdateXP (fires only on a correct answer) so Dax reacts there too.
  const handleAccessibleUpdateXP = (answerData: PostAnswerData[]): void => {
    if (showDax) triggerDaxCorrect()
    onUpdateXP(answerData)
  }

  const handleUnityEndGame = (json: string): void => {
    const answerData: PostAnswerData[] = JSON.parse(json).answers
    void onEndGame(answerData)
    setGameEnded(true)
  }

  if (multipleChoiceQuestionData !== null && multipleChoiceQuestionData !== undefined) {
    if (accessibilityView) {
      const game = (
        <AccessibleMultipleChoiceGame
          onEndGame={onEndGame}
          onUpdateXP={showDax ? handleAccessibleUpdateXP : onUpdateXP}
          questionsData={multipleChoiceQuestionData}
          onIncorrectAnswer={showDax ? triggerWrongBubble : undefined}
        />
      )
      if (!showDax) return game
      // Dax stands on the right side, horizontally centered over the 1/6-from-the-right
      // mark of the (full-width) health bar (left-[83.333%] == 5/6 across == 1/6 from the
      // right). Height is the measured 3/5 gap (falls back to 60vh until measured).
      return (
        <div ref={daxContainerRef} className='relative'>
          {game}
          <div
            className='pointer-events-none absolute left-[83.333%] -translate-x-1/2 w-[26%] z-10'
            style={{ height: daxAccessibleHeight ?? '60vh', top: daxAccessibleTop ?? '20%' }}
          >
            {/* Speech bubble by Dax's head, shown on a wrong answer and left up until the
                next answer. Dax stands centered in this (taller-than-him) box, so his head
                is ~upper third; the bubble sits just above-left of it with a tail pointing
                down at him. Keyed by wrongBubbleKey so re-answering wrong retypes it. */}
            {showWrongBubble &&
              <div
                data-testid='dax-speech-bubble'
                className='pointer-events-auto absolute left-1/2 top-[18%] -translate-x-full -translate-y-full z-20 max-w-[85%]'
              >
                <div className='relative w-[240px] rounded-2xl bg-white text-black text-sm md:text-base font-bold px-4 py-2 shadow-lg'>
                  <TypewriterText key={wrongBubbleKey} text='i am testing out this speech bubble,i am testing out this speech bubble,i am testing out this speech bubble,i am testing out this speech bubble' maxLines={3} />
                  <div className='absolute -bottom-2 right-5 h-0 w-0 border-l-8 border-r-8 border-t-8 border-l-transparent border-r-transparent border-t-white' />
                </div>
              </div>}
            <DaxAnimation
              className='w-full h-full'
              overrideClip='Waving.001'
              reaction={daxReaction}
              reactionKey={daxReactionKey}
              reduceMotion={reduceMotion}
            />
          </div>
        </div>
      )
    } else {
      return (
        <div className={clsx(responsiveUnity, 'flex justify-center relative')}>
          {!gameEnded &&
            <UnityPlayer
              onEndGame={handleUnityEndGame}
              onNewLightningModule={onNewLightningModule}
              onUpdateXP={handleUnityUpdateXP}
              payloadToUnity={payloadToUnity}
              trackedComponents={trackedComponents}
            />}
          {/* Unity renders position:fixed and fills the whole viewport (z-index 1), so
              Dax must also be fixed with a higher z-index to overlay it. The health bar
              and buttons are in-engine (not DOM) so we can't measure the gap; h-[58vh]
              approximates a 3/5-visible Dax: ~3/5 of a ~72vh in-engine gap, divided by
              the ~0.74 canvas fill so his body (not the box) reads as 3/5. */}
          {!gameEnded && showDax &&
            <DaxAnimation
              className='fixed bottom-[6vh] left-1/2 -translate-x-1/2 w-[300px] h-[58vh] z-50'
              overrideClip='Waving.001'
              reaction={daxReaction}
              reactionKey={daxReactionKey}
              reduceMotion={reduceMotion}
            />}
        </div>
      )
    }
  } else {
    return null
  }
}

export default React.memo(MultipleChoiceQuestions)
